import React, { useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../Assets/logo.jpg";
import "./AdminLogin.css";
import {adminLoginApi, fetchMyProfileApi} from "../../api/raceStudentsApi";
import {Modal} from "antd";

const AdminLogin = () => {
  const { login } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [deactivatedOpen, setDeactivatedOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    clearErrors,
  } = useForm();

  useEffect(() => {
    const handleUnload = () => {
      sessionStorage.clear();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  const handleInputChange = (fieldName) => {
    clearErrors(fieldName);
  };

  const onSubmit = async (data) => {
    sessionStorage.clear();

    try {
      const loginRes = await adminLoginApi(data);

      sessionStorage.setItem("adminToken", loginRes.token);
      sessionStorage.setItem("userRole", loginRes.admin?.userRole || "");  // if still returned

      let profile;
      try {
        profile = await fetchMyProfileApi();
      } catch (err) {
        sessionStorage.clear();
        throw new Error(
            err.response?.data?.error || "Could not load your profile."
        );
      }


      sessionStorage.setItem("userRole", profile.admin.userRole);
      sessionStorage.setItem("adminAcademy", profile.admin.academy);
      sessionStorage.setItem("userName", profile.admin.username);

      login(profile.admin);

      toast.success(
          `Welcome back, ${profile.admin.name || profile.admin.username}!`,
          { position: "top-center", autoClose: 3000 }
      );

      navigate("/");
    } catch (error) {
      const status = error.response?.status;
      const serverError = error.response?.data?.error;

      if (
          status === 403 &&
          serverError?.toLowerCase().includes("deactivated")
      ) {
        setDeactivatedOpen(true);
        return;
      }

      let errorMessage = error.message || "Login failed. Please try again.";

      if (status === 401) {
        errorMessage =
            "Invalid email or password. Please check your credentials.";
      } else if (status === 404) {
        errorMessage =
            "Account not found. Please check your email address.";
      } else if (status === 403) {
        errorMessage = "Access denied. Please contact administrator.";
      } else if (status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (serverError) {
        errorMessage = serverError;
      }

      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    }
  };

  return (
      <div className="login-page">
        {/* Ambient animated background blobs */}
        <div className="login-blob login-blob-1" />
        <div className="login-blob login-blob-2" />
        <div className="login-blob login-blob-3" />

        <div className="login-card">
          {/* Brand row */}
          <div className="login-brand">
            <img src={logo} alt="Academy" className="login-logo" />
            <span className="login-brand-name">Vidyarjana</span>
          </div>

          <div className="login-heading">
            <h1 className="login-title">Welcome back</h1>
            <p className="login-subtitle">
              Sign in to continue to your dashboard
            </p>
          </div>

          <form
              onSubmit={handleSubmit(onSubmit)}
              className="login-form"
              noValidate
          >
            <div className="lf-field">
              <label htmlFor="email" className="lf-label">
                Email
              </label>
              <div className="lf-input-wrap">
                <UserOutlined className="lf-icon" />
                <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...register("email", {
                      required: "Email address is required",
                      pattern: {
                        value:
                            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message:
                            "Please enter a valid email address",
                      },
                    })}
                    className="lf-input"
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setValue("email", e.target.value);
                      handleInputChange("email");
                    }}
                />
              </div>
              {errors.email && (
                  <span className="lf-error">{errors.email.message}</span>
              )}
            </div>

            <div className="lf-field">
              <div className="lf-label-row">
                <label htmlFor="password" className="lf-label">
                  Password
                </label>
                <Link
                    to="/forgot-password"
                    className="lf-forgot-inline"
                >
                  Forgot?
                </Link>
              </div>
              <div className="lf-input-wrap">
                <LockOutlined className="lf-icon" />
                <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message:
                            "Password must be at least 6 characters",
                      },
                    })}
                    className="lf-input"
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setValue("password", e.target.value);
                      handleInputChange("password");
                    }}
                />
                <button
                    type="button"
                    className="lf-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword
                          ? "Hide password"
                          : "Show password"
                    }
                >
                  {showPassword ? (
                      <EyeInvisibleOutlined />
                  ) : (
                      <EyeOutlined />
                  )}
                </button>
              </div>
              {errors.password && (
                  <span className="lf-error">
                                {errors.password.message}
                            </span>
              )}
            </div>

            <button
                type="submit"
                className="lf-submit"
                disabled={isSubmitting}
            >
              {isSubmitting ? (
                  <span className="lf-spinner" aria-label="Signing in" />
              ) : (
                  <>
                    <ThunderboltOutlined /> Sign in
                  </>
              )}
            </button>
          </form>

          <p className="login-footer">
            © {new Date().getFullYear()} Vidyarjana
          </p>
        </div>

        <Modal
            open={deactivatedOpen}
            onCancel={() => setDeactivatedOpen(false)}
            footer={null}
            centered
            width={420}
            className="account-inactive-modal"
            styles={{ body: { padding: 0 } }}
            closable={false}
            maskClosable={false}
        >
          <div className="aim-body">
            <div className="aim-icon">
              <CloseCircleFilled />
            </div>
            <h3 className="aim-title">Account Deactivated</h3>
            <p className="aim-desc">
              Your account has been deactivated by the admin. You won't be
              able to sign in until it's reactivated.
            </p>
            <p className="aim-hint">
              Please contact your admin for assistance.
            </p>
          </div>
          <div className="aim-footer">
            <button
                type="button"
                className="aim-btn"
                onClick={() => setDeactivatedOpen(false)}
            >
              OK
            </button>
          </div>
        </Modal>

        <ToastContainer position="top-center" autoClose={4000} />
      </div>
  );
};

export default AdminLogin;